import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import type { Section } from '../types/onboarding';
interface SectionNavProps {
  sections: Section[];
  activeId: string;
  isDone: (id: string) => boolean;
  doneCount: number;
  /** 0–1 page scroll progress driving the emerald indicator. */
  scrollProgress: number;
}
function ProgressTrack({
  progress,
  className = ''
}: {
  progress: number;
  className?: string;
}) {
  return <div className={`overflow-hidden rounded-full bg-white/10 ${className}`} aria-hidden="true" data-id="element-112" data-mp="1kw1ax7734fir">
      <div className="h-full rounded-full bg-[#10b981] transition-[width] duration-300 ease-out" style={{
      width: `${Math.round(progress * 100)}%`
    }} data-id="element-113" data-mp="1c1n13k5bpx71" />
    </div>;
}
function StepDot({
  number,
  done
}: {
  number: number;
  done: boolean;
}) {
  return <span aria-hidden="true" className={['flex h-6 w-6 shrink-0 items-center justify-center rounded-md border font-mono text-[10px] font-semibold transition-colors duration-200', done ? 'border-[#10b981]/50 bg-[#10b981] text-black' : 'border-white/10 bg-white/[0.06] text-white/60'].join(' ')} data-id="element-114" data-mp="24j8udjf34b3p">
      {done ? <CheckIcon className="h-3.5 w-3.5" data-id="element-115" data-mp="tdh8sijr2o6m" /> : String(number).padStart(2, '0')}
    </span>;
}
export function SectionNav({
  sections,
  activeId,
  isDone,
  doneCount,
  scrollProgress
}: SectionNavProps) {
  const [open, setOpen] = useState(false);
  const active = sections.find(section => section.id === activeId);
  return <>
      {/* Mobile: sticky compact jump bar */}
      <div className="sticky top-0 z-30 border-b border-white/10 bg-[#0a0a0a]/95 text-white backdrop-blur-md lg:hidden" data-id="element-116" data-mp="agg1us6mye01">
        <div className="px-5 pb-3 pt-3" data-id="element-117" data-mp="ei1b83jyoa5h">
          <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} className="flex min-h-[52px] w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#111214] px-4 text-left" data-id="element-118" data-mp="ej2ugvb4s2ya">
            <span className="min-w-0" data-id="element-119" data-mp="3hpuyufwa0cu8">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#10b981]" data-id="element-120" data-mp="3csv4h28sgyiy">
                Step {active ? String(active.number).padStart(2, '0') : '01'} /{' '}
                {String(sections.length).padStart(2, '0')}
              </span>
              <span className="mt-1 block truncate font-mono text-[14px] font-semibold tracking-tight text-white" data-id="element-121" data-mp="4dbfiff5i29p">
                {active ? active.navLabel : sections[0].navLabel}
              </span>
            </span>
            <ChevronDownIcon className={`h-5 w-5 shrink-0 text-white/60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-122" data-mp="3rpvlobtctb36" />
          </button>

          <ProgressTrack progress={scrollProgress} className="mt-3 h-1" data-id="element-123" data-mp="o1k9mv098ibu" />

          {open && <nav aria-label="Sections" className="mt-3 rounded-xl border border-white/10 bg-[#111214] p-2" data-id="element-124" data-mp="jabtzh8c00q8">
              <ul data-id="element-125" data-mp="qnuklti2dnww">
                {sections.map(section => <li key={section.id} data-id="element-126" data-mp="vlmf8va3fo4s">
                    <a href={`#${section.id}`} onClick={() => setOpen(false)} aria-current={section.id === activeId ? 'true' : undefined} className={['flex min-h-[48px] items-center gap-3 rounded-lg px-3 text-[15px] transition-colors duration-200', section.id === activeId ? 'bg-[#10b981]/10 font-semibold text-[#10b981]' : 'text-white/80'].join(' ')} data-id="element-127" data-mp="vmnznqd01mz6">
                      <StepDot number={section.number} done={isDone(section.id)} data-id="element-128" data-mp="1obl5uv3xrtbk" />
                      <span className="truncate" data-id="element-129" data-mp="1scx1bh9hr8ro">{section.navLabel}</span>
                    </a>
                  </li>)}
                <li data-id="element-130" data-mp="1jng89s00kz0s">
                  <a href="#need-a-hand" onClick={() => setOpen(false)} className="flex min-h-[48px] items-center gap-3 rounded-lg px-3 text-[15px] text-white/80" data-id="element-131" data-mp="1jmofsq1prheq">
                    <span className="h-6 w-6 shrink-0" aria-hidden="true" data-id="element-132" data-mp="1tcho9i6k7ger" />
                    Need a hand?
                  </a>
                </li>
              </ul>
            </nav>}
        </div>
      </div>

      {/* Desktop: sticky sidebar */}
      <nav aria-label="Sections" className="sticky top-8 hidden h-fit w-64 shrink-0 text-white lg:block xl:w-72" data-id="element-133" data-mp="wnsaa2y1z7y2">
        <div className="rounded-2xl border border-white/10 bg-[#111214] p-5 shadow-[0_18px_40px_-24px_rgba(0,0,0,0.9)]" data-id="element-134" data-mp="1ao3155zd4mj8">
          <div className="flex items-baseline justify-between" data-id="element-135" data-mp="15q1ekb2qa530">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#10b981]" data-id="element-136" data-mp="2m0dlco01jac2">
              Your progress
            </p>
            <p className="font-mono text-[11px] text-white/60" data-id="element-137" data-mp="2v2ps01ufsdv">
              {String(doneCount).padStart(2, '0')}/{String(sections.length).padStart(2, '0')}
            </p>
          </div>

          <div role="progressbar" aria-valuenow={doneCount} aria-valuemin={0} aria-valuemax={sections.length} aria-label="Onboarding progress" className="mt-3" data-id="element-138" data-mp="7rt6mplh2s0c">
            <ProgressTrack progress={scrollProgress} className="h-1.5" data-id="element-139" data-mp="mk4rtjxn73zq" />
          </div>

          <ul className="mt-5 space-y-1" data-id="element-140" data-mp="qkx1ufec9cx2">
            {sections.map(section => {
            const isActive = section.id === activeId;
            return <li key={section.id} data-id="element-141" data-mp="crfs1mbgfek8">
                  <a href={`#${section.id}`} aria-current={isActive ? 'true' : undefined} className={['relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-200', isActive ? 'bg-[#10b981]/10 font-semibold text-[#10b981]' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'].join(' ')} data-id="element-142" data-mp="hrk0cp27l2zd">
                    {isActive && <span aria-hidden="true" className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-[#10b981]" data-id="element-143" data-mp="1a8a2vwug3pbx" />}
                    <StepDot number={section.number} done={isDone(section.id)} data-id="element-144" data-mp="wf25tpnli1mu" />
                    <span className="truncate" data-id="element-145" data-mp="wd7mgfrz2hkv">{section.navLabel}</span>
                  </a>
                </li>;
          })}
          </ul>

          <a href="#need-a-hand" className="mt-5 flex items-center justify-center rounded-lg border border-[#10b981]/30 px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#10b981] transition-colors duration-200 hover:border-[#10b981] hover:bg-[#10b981]/10" data-id="element-146" data-mp="mhxjnptr2661">
            Need a hand?
          </a>
        </div>
      </nav>
    </>;
}